import { useMutation } from "@apollo/client";
import {
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminEditUserMutation,
  InternalAdminEditUserMutationVariables,
  InternalAdminUserFragment,
} from "gql/graphql";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import colors from "app/colors";
import { CALLOUT_KEY } from "modules/utils/Constants";
import {
  CancelIcon,
  CheckIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import { getMessageFromError } from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import INTERNAL_ADMIN_EDIT_USER_MUTATION from "../_networking/internal-admin-edit-user";

interface IProps {
  user: InternalAdminUserFragment;
}

const InternalAdminEditUserCallout = ({ user }: IProps) => {
  const { users } = useUsers();
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const { mode, setMode } = useGlobalEditing(
    `InternalAdminEditUserCallout-${user.id}`,
  );

  const [internalAdminEditUserMutationAPI, { loading }] = useMutation<
    InternalAdminEditUserMutation,
    InternalAdminEditUserMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      callout: user?.callout || "",
    },
  });

  const onClearClicked = useCallback(() => {
    setValue("callout", "");
  }, [setValue]);

  const callout = watch("callout");

  const isDuplicateCallout = useMemo(
    () =>
      !isEmpty(callout) &&
      users.filter((_user) => _user.id !== user.id && _user.callout === callout)
        .length > 0,
    [callout, user, users],
  );

  useEffect(() => {
    if (isDuplicateCallout) {
      setError("callout", {
        message: "This callout is already taken",
      });
    } else {
      clearErrors("callout");
    }
  }, [clearErrors, isDuplicateCallout, setError]);

  const onSubmit = useCallback(
    async ({ callout }: FieldValues) => {
      internalAdminEditUserMutationAPI({
        variables: {
          organizationId: user.organizationId,
          id: user.id,
          input: {
            callout,
          },
        },
        onError: (error) => {
          setError("callout", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setMode("display")();
        },
      });
    },
    [
      internalAdminEditUserMutationAPI,
      setError,
      setMode,
      user.id,
      user.organizationId,
    ],
  );

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-callout-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.callout} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <InputLeftElement h="100%" w={6}>
                  {CALLOUT_KEY}
                </InputLeftElement>
                <Input
                  id="callout"
                  size="sm"
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("callout", {
                    required: {
                      message: "The user's callout is required",
                      value: true,
                    },
                  })}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear callout"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                onClick={setMode("edit")}
                isDisabled={loading || isDuplicateCallout || isEmpty(callout)}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={loading}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.callout?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      _hover={{
        backgroundColor: colors.blue20,
      }}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      <Text fontSize={14} fontWeight={500} color={colors.gray50} w="full">
        {`${CALLOUT_KEY}${user.callout}`}
      </Text>
      <IconButton
        aria-label="Edit callout"
        icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(InternalAdminEditUserCallout);
